import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { Menu } from '../../../shared/interfaces/restaurant.interface';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';

@Component({
  selector: 'app-menu-edit-restaurant',
  imports: [
    MatIconModule,
    MatDividerModule,
    MatButtonModule,
    RouterLink,
    LoaderComponent,
    CommonModule,
  ],
  templateUrl: './menu-edit-restaurant.component.html',
  styleUrls: [
    './menu-edit-restaurant.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class MenuEditRestaurantComponent implements OnInit {
  constructor(
    private restaurantService: RestaurantService,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private storageService: StorageService,
    private rolesService: RolesService,
    private router: Router
  ) {}

  private _snackBar = inject(MatSnackBar);

  myroles: any = {};
  menus: Menu[] = [];
  currentYear: number = new Date().getFullYear();
  monthNames: string[] = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  displayedMonths: { month: string; monthIndex: number; menu?: Menu }[] = [];

  // Reference to track which month is being updated when uploading
  selectedMonthIndex: number | null = null;

  // Store PDFs temporarily before submitting
  pendingUploads: Map<number, File> = new Map();

  // Track menus to delete - change type from Set<string> to Set<number> to match the Menu id type
  pendingDeletes: Set<number> = new Set();

  // Add a map to store original file names
  originalFileNames: Map<string, string> = new Map();

  get isLoading() {
    return this.loaderService.getLoader();
  }

  loadRoles() {
    // Get permissions using the service method
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'general',
      this.storageService
    );

    if (permissions) {
      // Store permissions in myroles
      this.myroles = permissions;

      // Check write permission and redirect if needed
      this.rolesService.checkWritePermission(
        'general',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/dining/general',
        'No tienes permisos para añadir/editar los menús del comedor'
      );
    } else {
      //   console.log('No user roles found in storage');
    }
  }

  ngOnInit() {
    this.initializeMonths();
    this.fetchMenus();
    this.loadRoles();
    }

  initializeMonths() {
    this.displayedMonths = this.monthNames.map((monthName, index) => {
      return {
        month: monthName,
        monthIndex: index + 1,
        menu: undefined,
      };
    });
  }

  fetchMenus() {
    this.restaurantService.getRestaurantMenus().subscribe({
      next: (menus) => {
        //   console.log('Fetched restaurant menus:', menus);
        this.menus = menus;

        // If we have menus, set the current year to match the first menu's year for better UX
        //if (this.menus && this.menus.length > 0) {
        //  this.currentYear = this.menus[0].year;
        //  //    console.log('Setting current year to:', this.currentYear);
        //}

        this.updateDisplayedMonths();
        //   console.log('Updated displayed months:', this.displayedMonths);

        // Force Angular to detect changes and update the view
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error fetching restaurant menus:', error);
      },
    });
  }

  updateDisplayedMonths() {
    this.displayedMonths = this.monthNames.map((monthName, index) => {
      const monthIndex = index + 1;
      const monthMenu = this.menus.find(
        (menu) =>
          menu.month === monthIndex &&
          menu.year === this.currentYear &&
          !menu.is_deleted
      );

      //   console.log(`Month ${monthIndex} (${monthName}) - Menu:`, monthMenu);

      return {
        month: monthName,
        monthIndex: monthIndex,
        menu: monthMenu,
      };
    });
  }

  changeYear(increment: number) {
    this.currentYear += increment;
    this.updateDisplayedMonths();
  }

  getFilename(url: string | null): string {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1];
  }

  openPdfFile(url: string | null) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  /**
   * Opens the file upload dialog for a specific month
   * @param monthIndex The index of the month for which to upload a menu
   */
  openFileUploader(monthIndex: number): void {
    this.selectedMonthIndex = monthIndex;
    // Use timeout to ensure Angular's change detection cycle completes
    setTimeout(() => {
      const fileInput = document.getElementById(
        'menuFileInput'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    }, 0);
  }

  /**
   * Handles file selection for menu upload
   * @param event The file input change event
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0 && this.selectedMonthIndex) {
      const file = input.files[0];

      // Validate file is PDF
      if (file.type !== 'application/pdf') {
        this.snackBar.open('Por favor seleccione un archivo PDF.', 'Cerrar', {
          duration: 3000,
        });
        return;
      }

      // Store the file for the selected month
      this.pendingUploads.set(this.selectedMonthIndex, file);

      // Update UI to show pending upload
      const monthIndex = this.displayedMonths.findIndex(
        (m) => m.monthIndex === this.selectedMonthIndex
      );

      if (monthIndex >= 0) {
        // Create a temporary menu object for display purposes
        if (!this.displayedMonths[monthIndex].menu) {
          const tempId = 'pending-' + this.selectedMonthIndex;
          this.displayedMonths[monthIndex].menu = {
            id: tempId,
            month: this.selectedMonthIndex as number,
            year: this.currentYear,
            pdf_file: URL.createObjectURL(file),
            is_active: true,
            is_deleted: false,
            date_created: new Date().toISOString(),
            date_modified: new Date().toISOString(),
          };

          // Store the original file name for this temporary ID
          this.originalFileNames.set(tempId, file.name);
        } else {
          // Replace existing file URL for preview
          const menuId = this.displayedMonths[monthIndex].menu!.id;
          this.displayedMonths[monthIndex].menu!.pdf_file =
            URL.createObjectURL(file);

          // Update the original file name
          this.originalFileNames.set(String(menuId), file.name);
        }
      }

      // Reset the input for future uploads
      input.value = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Get the display filename for a menu
   * @param url The URL of the PDF file
   * @param menuId The ID of the menu
   * @returns The filename to display
   */
  getDisplayFilename(url: string | null, menuId: string | number): string {
    // First check if we have an original name stored for this menu
    const originalName = this.originalFileNames.get(String(menuId));
    if (originalName) {
      return originalName;
    }

    // If not, use the standard filename extraction
    return this.getFilename(url);
  }

  /**
   * Deletes a menu
   * @param monthData The month data containing the menu to delete
   */
  deleteMenu(monthData: any): void {
    if (monthData.menu) {
      const menuId = monthData.menu.id;
      // Check if this is a pending upload (string id starting with 'pending')
      if (typeof menuId === 'string' && menuId.startsWith('pending')) {
        // Remove from pending uploads
        this.pendingUploads.delete(monthData.monthIndex);

        // Reset the menu to undefined
        monthData.menu = undefined;
      } else {
        // Add to pending deletes for existing menus - cast to number
        this.pendingDeletes.add(Number(menuId));

        // Mark as visually deleted
        monthData.menu.is_deleted = true;
      }
      this.cdr.detectChanges();
    }
  }

  /**
   * Save all changes (uploads and deletes)
   */
  saveChanges(): void {
    this.loaderService.showLoader();

    // Process deletes first
    const deletePromises: Promise<any>[] = [];
    this.pendingDeletes.forEach((menuId) => {
      const deletePromise = new Promise((resolve, reject) => {
        // menuId is already a number here, no need to convert
        this.restaurantService.deleteRestaurantMenu(menuId).subscribe({
          next: () => resolve(menuId),
          error: (err) => reject(err),
        });
      });
      deletePromises.push(deletePromise);
    });

    // Process uploads
    const uploadPromises: Promise<any>[] = [];
    this.pendingUploads.forEach((file, monthIndex) => {
      const uploadPromise = new Promise((resolve, reject) => {
        const formData = new FormData();
        // The file is appended as is, the backend will preserve the original filename
        formData.append('pdf_file', file);

        // Format month with leading zero (1 -> 01, 12 -> 12)
        const monthStr = monthIndex.toString().padStart(2, '0');
        formData.append('month', monthStr);
        formData.append('year', this.currentYear.toString());
        formData.append('active', 'true');

        this.restaurantService.postRestaurantMenu(formData).subscribe({
          next: (result) => {
            // If we get a result, store the original filename for this new menu
            if (result && result.length > 0) {
              this.originalFileNames.set(String(result[0].id), file.name);
            }
            resolve(result);
          },
          error: (err) => reject(err),
        });
      });
      uploadPromises.push(uploadPromise);
    });

    // Process all promises
    Promise.all([...deletePromises, ...uploadPromises])
      .then(() => {
        // Clear pending operations
        this.pendingUploads.clear();
        this.pendingDeletes.clear();

        // Refresh data
        this.fetchMenus();
        this.snackBar.open('Cambios guardados correctamente', 'Cerrar', {
          duration: 3000,
        });
      })
      .catch((error) => {
        console.error('Error saving changes:', error);
        this.snackBar.open('Error al guardar los cambios', 'Cerrar', {
          duration: 3000,
        });
        this.loaderService.hideLoader();
      })
      .finally(() => {
        this.loaderService.hideLoader();
      });
  }

  /**
   * Cancel all pending changes
   */
  cancelChanges(): void {
    this.pendingUploads.clear();
    this.pendingDeletes.clear();
    this.fetchMenus(); // Reload original data
    this.snackBar.open('Cambios cancelados', 'Cerrar', {
      duration: 3000,
    });
  }

  // Helper function to check if a menu exists for debugging
  hasMenu(index: number): boolean {
    return !!this.displayedMonths[index]?.menu;
  }
}
